/* ==========================================================================
   <full-width-media> — tile video controller
   - silent autoplay on loop with an image poster/fallback
   - optional play/pause control per video
   - respects prefers-reduced-motion
   - pauses while off-screen to save resources
   - supports multiple videos in one section (mosaic tiles)
   ========================================================================== */
(function () {
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class VideoControl {
    constructor(video, toggle) {
      this.video = video;
      this.toggle = toggle;
      // Paused state we want to preserve across intersection changes.
      this.userPaused = REDUCED_MOTION;

      video.addEventListener('play', () => this._setState(true));
      video.addEventListener('pause', () => this._setState(false));

      if (toggle) {
        toggle.addEventListener('click', () => {
          if (video.paused) {
            this.userPaused = false;
            this._play();
          } else {
            this.userPaused = true;
            video.pause();
          }
        });
      }

      if ('IntersectionObserver' in window) {
        this._observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                if (!this.userPaused) this._play();
              } else {
                video.pause();
              }
            });
          },
          { threshold: 0.15 }
        );
        this._observer.observe(video);
      } else if (!this.userPaused) {
        this._play();
      }

      this._setState(!video.paused);
    }

    _play() {
      const attempt = this.video.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(() => {
          /* autoplay can be blocked — leave the poster visible */
        });
      }
    }

    _setState(playing) {
      if (!this.toggle) return;
      this.toggle.dataset.playing = playing ? 'true' : 'false';
      this.toggle.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
    }

    destroy() {
      if (this._observer) this._observer.disconnect();
    }
  }

  class FullWidthMedia extends HTMLElement {
    connectedCallback() {
      this.controls = [];
      this.querySelectorAll('.fwiv__video').forEach((video) => {
        const tile = video.closest('.fwiv__tile') || this;
        const toggle = tile.querySelector('[data-fwiv-toggle]');
        this.controls.push(new VideoControl(video, toggle));
      });
    }

    disconnectedCallback() {
      this.controls.forEach((control) => control.destroy());
    }
  }

  if (!customElements.get('full-width-media')) {
    customElements.define('full-width-media', FullWidthMedia);
  }
})();
